import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Form,
  Header,
  Message,
  Segment,
  Card,
} from 'semantic-ui-react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  API,
  copy,
  showError,
  showSuccess,
  timestamp2string,
} from '../../helpers';
import { renderQuotaWithPrompt } from '../../helpers/render';

const EditToken = () => {
  const { t } = useTranslation();
  const params = useParams();
  const tokenId = params.id;
  const isEdit = tokenId !== undefined;
  const [loading, setLoading] = useState(isEdit);
  const [modelOptions, setModelOptions] = useState([]);
  const [channelOptions, setChannelOptions] = useState([]);
  const originInputs = {
    name: '',
    remain_quota: isEdit ? 0 : 500000,
    expired_time: -1,
    unlimited_quota: false,
    models: [],
    subnet: '',
    channel_id: '',
  };
  const [inputs, setInputs] = useState(originInputs);
  const { name, remain_quota, expired_time, unlimited_quota } = inputs;
  const navigate = useNavigate();
  const handleInputChange = (e, { name, value }) => {
    setInputs((inputs) => ({ ...inputs, [name]: value }));
  };
  const handleCancel = () => {
    navigate('/token');
  };
  const setExpiredTime = (month, day, hour, minute) => {
    let now = new Date();
    let timestamp = now.getTime() / 1000;
    let seconds = month * 30 * 24 * 60 * 60;
    seconds += day * 24 * 60 * 60;
    seconds += hour * 60 * 60;
    seconds += minute * 60;
    if (seconds !== 0) {
      timestamp += seconds;
      setInputs({ ...inputs, expired_time: timestamp2string(timestamp) });
    } else {
      setInputs({ ...inputs, expired_time: -1 });
    }
  };

  const setUnlimitedQuota = () => {
    setInputs({ ...inputs, unlimited_quota: !unlimited_quota });
  };

  const loadToken = async () => {
    try {
      let res = await API.get(`/api/token/${tokenId}`);
      const { success, message, data } = res.data || {};
      if (success && data) {
        if (data.expired_time !== -1) {
          data.expired_time = timestamp2string(data.expired_time);
        }
        if (data.models === '' || data.models === null) {
          data.models = [];
        } else if (typeof data.models === 'string') {
          data.models = data.models.split(',');
        }
        // 确保 channel_id 是数字类型
        if (data.channel_id) {
          data.channel_id = parseInt(data.channel_id);
        }
        setInputs(data);
      } else {
        showError(message || 'Failed to load token');
      }
    } catch (error) {
      showError(error.message || 'Network error');
    }
    setLoading(false);
  };

  const loadAvailableModels = async () => {
    try {
      let res = await API.get(`/api/user/available_models`);
      const { success, message, data } = res.data || {};
      if (success && data) {
        let options = data.map((model) => {
          return {
            key: model,
            text: model,
            value: model,
          };
        });
        setModelOptions(options);
      } else {
        showError(message || 'Failed to load models');
      }
    } catch (error) {
      showError(error.message || 'Network error');
    }
  };

  const loadChannels = async () => {
    try {
      // 普通用户使用 /api/user/available_channels，管理员使用 /api/channel/
      let res = await API.get(`/api/user/available_channels`);
      const { success, message, data } = res.data || {};
      if (success && data) {
        let options = [];
        data.forEach((channel) => {
          // 过滤禁用的渠道（status !== 1 表示禁用）
          if (channel.status !== 1) return;
          options.push({
            key: channel.id,
            text: `${channel.id} - ${channel.name} (${channel.type_name || channel.type})`,
            value: channel.id,
            models: channel.models ? channel.models.split(',') : [],
          });
        });
        setChannelOptions(options);
      } else {
        showError(message || 'Failed to load channels');
      }
    } catch (error) {
      showError(error.message || 'Network error');
    }
  };

  // 根据选中的渠道更新模型选项
  // isInit: 是否是初始化调用（编辑时），初始化时不清空已选模型
  const updateModelOptionsByChannel = (channelId, isInit = false) => {
    if (!channelId) {
      // 未选择渠道，清空模型选项
      setModelOptions([]);
      if (!isInit) {
        setInputs((inputs) => ({ ...inputs, models: [] }));
      }
      return;
    }
    
    // 找到选中的渠道
    const selectedChannel = channelOptions.find(ch => ch.value === channelId);
    if (selectedChannel && selectedChannel.models) {
      let options = selectedChannel.models.map((model) => {
        return {
          key: model,
          text: model,
          value: model,
        };
      });
      setModelOptions(options);
      // 只有在用户手动切换渠道时才清空已选模型
      if (!isInit) {
        setInputs((inputs) => ({ ...inputs, models: [] }));
      }
    }
  };

  useEffect(() => {
    if (isEdit) {
      loadToken().catch((error) => {
        showError(error.message || 'Failed to load token');
        setLoading(false);
      });
    }
    loadChannels().catch((error) => {
      showError(error.message || 'Failed to load channels');
    });
  }, []);

  // 当渠道选项加载完成后，如果是编辑模式且绑定了渠道，更新模型列表
  useEffect(() => {
    if (isEdit && inputs.channel_id && channelOptions.length > 0) {
      updateModelOptionsByChannel(inputs.channel_id, true);
    }
  }, [channelOptions, inputs.channel_id]);

  const submit = async () => {
    if (!isEdit && inputs.name === '') return;
    if (!inputs.channel_id) {
      showError('请选择绑定渠道');
      return;
    }
    if (!inputs.models || inputs.models.length === 0) {
      showError('请选择至少一个模型');
      return;
    }
    let localInputs = inputs;
    localInputs.remain_quota = parseInt(localInputs.remain_quota);
    if (localInputs.expired_time !== -1) {
      let time = Date.parse(localInputs.expired_time);
      if (isNaN(time)) {
        showError(t('token.edit.messages.expire_time_invalid'));
        return;
      }
      localInputs.expired_time = Math.ceil(time / 1000);
    }
    localInputs.models = localInputs.models.join(',');
    let res;
    if (isEdit) {
      res = await API.put(`/api/token/`, {
        ...localInputs,
        id: parseInt(tokenId),
      });
    } else {
      res = await API.post(`/api/token/`, localInputs);
    }
    const { success, message } = res.data;
    if (success) {
      if (isEdit) {
        showSuccess(t('token.edit.messages.update_success'));
      } else {
        showSuccess(t('token.edit.messages.create_success'));
        setInputs(originInputs);
      }
    } else {
      showError(message);
    }
  };

  return (
    <div className='dashboard-container'>
      <Card fluid className='chart-card'>
        <Card.Content>
          <Card.Header className='header'>
            {isEdit ? t('token.edit.title_edit') : t('token.edit.title_create')}
          </Card.Header>
          <Form loading={loading} autoComplete='new-password'>
            <Form.Field required>
              <Form.Dropdown
                label='绑定渠道 *'
                placeholder='请选择要绑定的渠道'
                name='channel_id'
                fluid
                search
                selection
                onChange={(e, { name, value }) => {
                  handleInputChange(e, { name, value });
                  // 渠道改变时更新模型列表
                  updateModelOptionsByChannel(value);
                }}
                value={inputs.channel_id}
                autoComplete='new-password'
                options={channelOptions}
                required
              />
              <small>令牌将强制使用指定渠道，模型列表只显示该渠道支持的模型</small>
            </Form.Field>
            <Form.Field>
              <Form.Input
                label={t('token.edit.name')}
                name='name'
                placeholder={t('token.edit.name_placeholder')}
                onChange={handleInputChange}
                value={name}
                autoComplete='new-password'
                required={!isEdit}
              />
            </Form.Field>
            <Form.Field required>
              <Form.Dropdown
                label='模型范围 *'
                placeholder='请选择该渠道支持的模型'
                name='models'
                fluid
                multiple
                search
                onLabelClick={(e, { value }) => {
                  copy(value).then();
                }}
                selection
                onChange={handleInputChange}
                value={inputs.models}
                autoComplete='new-password'
                options={modelOptions}
                disabled={!inputs.channel_id}
              />
              {!inputs.channel_id && <small style={{color: '#999'}}>请先选择绑定渠道</small>}
            </Form.Field>
            <Form.Field>
              <Form.Input
                label={t('token.edit.ip_limit')}
                name='subnet'
                placeholder={t('token.edit.ip_limit_placeholder')}
                onChange={handleInputChange}
                value={inputs.subnet}
                autoComplete='new-password'
              />
            </Form.Field>
            <Form.Field>
              <Form.Input
                label={t('token.edit.expire_time')}
                name='expired_time'
                placeholder={t('token.edit.expire_time_placeholder')}
                onChange={handleInputChange}
                value={expired_time}
                autoComplete='new-password'
                type='datetime-local'
              />
            </Form.Field>
            <div style={{ lineHeight: '40px' }}>
              <Button
                type={'button'}
                onClick={() => {
                  setExpiredTime(0, 0, 0, 0);
                }}
              >
                {t('token.edit.buttons.never_expire')}
              </Button>
              <Button
                type={'button'}
                onClick={() => {
                  setExpiredTime(1, 0, 0, 0);
                }}
              >
                {t('token.edit.buttons.expire_1_month')}
              </Button>
              <Button
                type={'button'}
                onClick={() => {
                  setExpiredTime(0, 1, 0, 0);
                }}
              >
                {t('token.edit.buttons.expire_1_day')}
              </Button>
              <Button
                type={'button'}
                onClick={() => {
                  setExpiredTime(0, 0, 1, 0);
                }}
              >
                {t('token.edit.buttons.expire_1_hour')}
              </Button>
              <Button
                type={'button'}
                onClick={() => {
                  setExpiredTime(0, 0, 0, 1);
                }}
              >
                {t('token.edit.buttons.expire_1_minute')}
              </Button>
            </div>
            <Message>{t('token.edit.quota_notice')}</Message>
            <Form.Field>
              <Form.Input
                label={`${t('token.edit.quota')}${renderQuotaWithPrompt(
                  remain_quota,
                  t
                )}`}
                name='remain_quota'
                placeholder={t('token.edit.quota_placeholder')}
                onChange={handleInputChange}
                value={remain_quota}
                autoComplete='new-password'
                type='number'
                disabled={unlimited_quota}
              />
            </Form.Field>
            <Button
              type={'button'}
              onClick={() => {
                setUnlimitedQuota();
              }}
            >
              {unlimited_quota
                ? t('token.edit.buttons.cancel_unlimited')
                : t('token.edit.buttons.unlimited_quota')}
            </Button>
            <Button floated='right' positive onClick={submit}>
              {t('token.edit.buttons.submit')}
            </Button>
            <Button floated='right' onClick={handleCancel}>
              {t('token.edit.buttons.cancel')}
            </Button>
          </Form>
        </Card.Content>
      </Card>
    </div>
  );
};

export default EditToken;
